type UnknownRecord = Record<string, unknown>;

/**
 * Normalizes a stored value so that representation differences do not count as amendments:
 * empty strings, null and undefined are the same empty value, numbers compare as strings, text
 * is compared without surrounding whitespace, and dates compare by their ISO representation.
 */
export function normalizeValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeValue(item));
  }

  if (isDateLike(value)) {
    return normalizeDate(value);
  }

  const record = asRecord(value);

  if (record) {
    return Object.keys(record)
      .sort()
      .reduce<UnknownRecord>((normalized, key) => {
        normalized[key] = normalizeValue(record[key]);
        return normalized;
      }, {});
  }

  return null;
}

export function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Reads `record[key]` when `value` is a record, else undefined.
 */
export function readKey(value: unknown, key: string): unknown {
  return asRecord(value)?.[key];
}

function isDateLike(value: unknown): value is { toISOString(): string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toISOString?: unknown }).toISOString === 'function'
  );
}

function normalizeDate(value: { toISOString(): string }): string | null {
  try {
    return value.toISOString();
  } catch {
    return null;
  }
}
